// <s-card-swatches> — colour swatches on product cards. Hover (mouse), focus or command --pin (tap) picks a colour: its photo slides into the card image and quick add uses it. Emits swatch:change on the card.
class SCardSwatches extends HTMLElement {
  #ac;
  #media;
  #original;
  #pinned = null;

  connectedCallback() {
    this.#media = this.closest('article')?.querySelector('a img');
    if (!this.#media) return;
    this.#original = { src: this.#media.currentSrc || this.#media.src, srcset: this.#media.srcset };
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('command', (event) => {
      if (event.command === '--pin') this.#pin(event.source);
    }, { signal });
    this.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const button = event.target.closest('button[data-color]');
      if (button) this.#pin(button);
    }, { signal });
    this.addEventListener('focusin', (event) => {
      const button = event.target.closest('button[data-color]');
      if (button) this.#pin(button);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #pin(button) {
    if (!(button instanceof HTMLElement) || button === this.#pinned) return;
    this.#pinned = button;
    for (const swatch of this.querySelectorAll('button')) swatch.setAttribute('aria-pressed', String(swatch === button));
    if (button.dataset.src) this.#slideTo({ src: button.dataset.src, srcset: button.dataset.srcset });
    this.dispatchEvent(new CustomEvent('swatch:change', { bubbles: true, detail: { color: button.dataset.color } }));
  }

  #slideTo({ src, srcset }) {
    const base = this.#media;
    const frame = base.parentElement;
    const previous = [...frame.querySelectorAll('[data-swatch-layer]')];
    if (src === this.#original.src && srcset === this.#original.srcset) {
      previous.forEach((layer) => layer.remove());
      return;
    }

    // A layer over the original keeps the LCP image untouched and lets the swap animate.
    const layer = base.cloneNode(false);
    layer.removeAttribute('fetchpriority');
    layer.removeAttribute('loading');
    layer.dataset.swatchLayer = '';
    layer.alt = '';
    layer.setAttribute('aria-hidden', 'true');
    layer.classList.add('absolute', 'inset-0');
    if (srcset) layer.srcset = srcset;
    layer.src = src;
    frame.appendChild(layer);

    const cleanup = () => previous.forEach((old) => old.remove());
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      cleanup();
      return;
    }
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    layer
      .animate(
        [{ opacity: 0, transform: `translateX(${6 * rtl}%)` }, { opacity: 1, transform: 'translateX(0)' }],
        { duration: 300, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      )
      .finished.then(cleanup, cleanup);
  }
}

if (!customElements.get('s-card-swatches')) customElements.define('s-card-swatches', SCardSwatches);
